import dayjs from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc.js';
import timezonePlugin from 'dayjs/plugin/timezone.js';
import LocalizedFormatPlugin from 'dayjs/plugin/localizedFormat.js';
import { dayjsLocaleConverter } from "../func/index.js";
import 'dayjs/locale/en.js';
import 'dayjs/locale/fr.js';
dayjs.extend(LocalizedFormatPlugin);
dayjs.extend(timezonePlugin);
dayjs.extend(utcPlugin);
export const durationToDayjs = {
    days: 'day',
    weeks: 'week',
    months: 'month',
    years: 'year',
};
let currentLocale = 'en-US';
/**
 * Sets the locale.
 * @return void
 * @param locale
 */
export const setLocale = (locale) => {
    currentLocale = locale;
    dayjs.locale(dayjsLocaleConverter(locale));
};
/**
 * Returns the locale that has been set.
 * @return {String}, e.g. 'en-US'
 */
export const getLocale = () => currentLocale;
export const utcDate = (now = new Date()) => dayjs(now).utc();
export const utcIsoDate = (now = new Date()) => utcDate(now).format();
export const utcStartOfDayIsoDate = (now) => utcDate(now).startOf('day').format();
export const utcStartOfFutureDayIsoDate = (now, days = 0) => utcDate(now).add(days, 'days').startOf('day').format();
export const mockCreatedIso = ({ now = new Date(), numberOf = 0, unit = 'day', } = {}) => {
    if (numberOf > 0) {
        return utcDate(now).add(numberOf, unit).format();
    }
    else if (numberOf < 0) {
        return utcDate(now).subtract(Math.abs(numberOf), unit).format();
    }
    return utcDate(now).format();
};
export const reminderEventIso = (now, numberOf = 0, unit = 'day') => {
    if (numberOf > 0) {
        return utcDate(now).add(numberOf, unit).startOf('day').format();
    }
    else if (numberOf < 0) {
        return utcDate(now)
            .subtract(Math.abs(numberOf), unit)
            .startOf('day')
            .format();
    }
    return utcDate(now).startOf('day').format();
};
export const reminderEventLoopDateAfter = (now, days = 0, unit = 'day') => utcDate(now).add(days, unit).startOf('day');
export const reminderEventLoopDateBefore = (now, days = 0, unit = 'day') => utcDate(now).subtract(days, unit).startOf('day');
export const reminderEventLoopDateOn = (now) => utcDate(now).startOf('day');
/**
 * Gets a date object from a date string.
 * @return {Date} e.g. 2019-02-26T00:37:56.355Z
 * @param dateStr
 */
export const getDateFromString = (dateStr) => dayjs(dateStr).toDate();
/**
 * Returns a date string in a given format.
 * @return {string}, e.g. '2019-02-26T00:37:56.355Z'
 * @param format
 * @param dateStr
 */
export const getFormattedDateFromString = (format, dateStr = null) => dateStr ? dayjs(dateStr).format(format) : dayjs().format(format);
/**
 * Adds a number of duration to a date.
 * @param {number} amount e.g. 1
 * @param {string} duration e.g. 'days'
 * @return {string} e.g. '2022-01-03T15:00:00Z'
 */
export const utcAddDurationToDate = (amount, duration) => utcDate().add(amount, duration).format();
/**
 * Converts date to given timezone.
 * @param {string} date e.g. '2022-01-01T20:00:00+0500'
 * @param {string} timezone e.g. 'Europe/London'
 * @return {string} e.g. '2022-01-01T10:00:00-05:00'
 */
export const timezonedSetIso = ({ date, timezone, }) => dayjs(date).tz(timezone).format();
/**
 * Returns a formatted date string in the given timezone.
 * @param {string} date
 * @param {string} timezone
 * @returns {string} e.g. 'Friday 31 December 2021'
 */
export const getReminderEventDate = ({ date = new Date(), timezone, }) => dayjs(date).tz(timezone).format('dddd D MMMM YYYY');
/**
 * Returns a formatted date string in the given timezone.
 * @param {string} date
 * @param {string} timezone
 * @returns {string} e.g. '31 December 2021'
 */
export const getReminderEventInfoDate = ({ date = new Date(), timezone }) => dayjs(date).tz(timezone).format('D MMMM YYYY');
export const genDateForDatepicker = ({ daysAdjustment = 0, } = {}) => dayjs(new Date())
    .tz('UTC')
    .add(daysAdjustment, 'day')
    .format('DD-MMM-YYYY');
/**
 * Used to set the date on a DatePicker component.
 * @param {string} date - priorityTargetDate in UTC, e.g. '2022-02-04T00:00:00.000Z'
 * @param {string} timezone - timezone of the logged in user, e.g. 'Europe/London'
 * @returns {Date}
 */
export const getDateForDatepicker = ({ date, timezone, }) => {
    const dateStr = dayjs(date).tz('UTC').tz(timezone).format();
    return dayjs(`${dateStr.substr(0, 10)}T00:00:00.000Z`)
        .utc()
        .toDate();
};
/**
 * Used by a prioritized item.
 * @param {string} priorityTargetDate priorityTargetDate in UTC, e.g. '2022-01-01T00:00:00Z'
 * @param {string} timezone timezone of the logged in user, e.g. 'Europe/London'
 * @returns {boolean}
 */
export const isPrioritizedItemOverdue = ({ priorityTargetDate, timezone }) => {
    if (priorityTargetDate) {
        const todayInTimezone = dayjs().tz(timezone).format();
        const startOfTodayInUtc = utcDate(`${todayInTimezone.substr(0, 10)}T00:00:00.000`).toDate();
        const targetDateInUtc = utcDate(priorityTargetDate).toDate();
        return startOfTodayInUtc > targetDateInUtc;
    }
    return false;
};
/**
 * Converts value from DatePicker component to a saveable UTC string.
 * @param {Date} date - Date object supplied by DatePicker.
 * @param timezone - Logged in user timezone, e.g. 'Europe/London'
 * @returns {string} e.g. '2022-01-01T15:00:00Z'
 */
export const getSaveableUtcFromDatePicker = ({ date = new Date(), timezone }) => {
    const timezonedIso = timezonedSetIso({
        date,
        timezone,
    });
    return timezonedSetIso({ date: timezonedIso, timezone: 'UTC' });
};
/**
 * Produces the marker text in the prioritized items list.
 * @param date {string} - UTC date in iso format.
 * @param timezone {string} - timezone of the logged in user, e.g. 'Europe/London'
 * @returns {string} e.g. '01-Jan-22'
 */
export const getPrioritizedListDayMarker = ({ date, timezone }) => dayjs(date).tz(timezone).format('DD-MMM-YY');
/**
 * Used when dropping a item into a dated droppable in the prioritized list.
 * @param ymd {string} e.g. '2022-01-01'
 * @param timezone {string} e.g. 'Europe/London'
 * @returns {string} in UTC, e.g. '2022-01-01T05:00:00Z'
 */
export const getSaveableUtcFromDatedDroppable = ({ ymd, timezone }) => {
    const timezonedDate = dayjs(`${ymd}T00:00:00`)
        .tz(timezone, true)
        .format();
    return dayjs(timezonedDate).tz('UTC').format();
};
/**
 * Given a timezone, it will return the ISO date of now.
 * @returns {string} e.g. '2021-12-31T14:00:00-05:00'
 * @param timezone
 */
export const getNowInTimezone = (timezone) => dayjs().tz(timezone).format();
/**
 * Used when retrieving a list of reminder events before the end of today.
 * @returns {string} e.g. '2022-01-01T15:00:00Z'
 */
export const getReminderEventsBeginDate = () => getNowInTimezone('UTC');
/**
 * Given a date in ISO format, it will return the milliseconds since unix epoch.
 * @returns {number} e.g. 1640995200000
 * @param dateISO
 */
export const getMillisecondsSinceUnixEpochFromTimestampInMilliseconds = (dateISO) => dayjs(dateISO).valueOf();
/**
 * Given a timezone, it will return the Y-m-dT23:23:59 of now in that timezone.
 * @returns {string} e.g. '2021-12-31T23:59:59-05:00'
 * @param timezone
 */
export const getNowEndOfDayInTimezone = (timezone) => {
    const ymd = getNowInTimezone(timezone);
    return ymd.replace(/T\d\d:\d\d:\d\d/, 'T23:59:59');
};
/**
 * Given a timezone, it will return the equivalents of Y-m-dT23:23:59 but converted to UTC zone.
 * @returns {string} e.g. '2022-01-01T04:59:59Z'
 * @param timezone
 */
export const getNowIsoEndOfDayInUtc = (timezone) => {
    const date = getNowEndOfDayInTimezone(timezone);
    return dayjs(date).tz('UTC').format();
};
/**
 * Test if the system timezone matches the given timezone
 * @return {boolean}
 * @param timezone
 */
export const isBrowserInUsersTimezone = (timezone) => dayjs.tz.guess() === timezone;
/**
 * Used to display the target date, e.g. item settings modal.
 * @param {string} date - priorityTargetDate in UTC, e.g. '2022-02-04T00:00:00.000Z'
 * @param {string} timezone - timezone of the logged in user, e.g. 'Europe/London'
 * @returns {string} e.g. '31 December 2021'
 */
export const targetDate = ({ date, timezone }) => {
    return dayjs(date).tz('UTC').tz(timezone).format('D MMMM YYYY');
};
/**
 * Used when setting the JWT cookie expiry date.
 * @param {string} tokenExpiry - seconds
 * @returns {Date}
 */
export const getJwtCookieExpiryDate = ({ tokenExpiry, }) => {
    return dayjs(new Date()).add(tokenExpiry, 'second').utc().toDate();
};
/**
 * Get minutes between two dates.
 * @param {string} date1 - e.g. '2022-02-04T00:00:00.000Z'
 * @param {string} date2 - e.g. '2022-01-04T00:00:00.000Z'
 * @returns {number} e.g. 10
 */
export const minutesBetween = (date1, date2) => {
    const date1DayJs = dayjs(date1).utc();
    const date2DayJs = dayjs(date2).utc();
    return Math.abs(date1DayJs.diff(date2DayJs, 'minute'));
};
//# sourceMappingURL=index.js.map